import { useTranslation } from "react-i18next";
import { SelectInput, TextLargeInput } from "./Input-Components";
import { useContext, useEffect, useState } from "react";
import {
  requestBanHistoryMember,
  requestBanHistoryTemp,
  requestBanTempUser,
  requestUnbanMember,
  requestUnbanTempUser,
} from "../requests/ticket_requests";
import { ContentContext } from "../contexts/ContentContext";
import { loadingActive } from "./Background";
import { TicketButton } from "./DeveloperComponents";
import { AccountContext } from "../contexts/AccountContexts";
import { formatTimestamp } from "../utils";
import { darkAndLightThemeDefault } from "./ThemeComponents";
import { requestBanMember } from "../requests/ticket_requests";

const banTimes = {
  "times.7_days": "7 days",
  "times.15_days": "15 days",
  "times.1_month": "1 month",
  "times.3_months": "3 months",
  "times.6_months": "6 months",
  "times.perma": "perma",
};

export const BanModal = ({ anonUser, ticketInfo, setTicketInformation }) => {
  const { t } = useTranslation("edit");
  const { openErrorModal } = useContext(ContentContext);

  const [banReason, setBanReasion] = useState("");
  const [banTime, setBanTime] = useState(banTimes["times.7_days"]);

  const handleConfirm = () => {
    loadingActive(true);
    if (!anonUser.banned) {
      if (anonUser.type === "temp") {
        requestBanTempUser({
          ticketId: ticketInfo.id,
          banReason: banReason,
          banTime: banTime,
        })
          .then((data) => {
            setTicketInformation({
              ticketData: data.ticket,
              userData: data.user,
            });
          })
          .catch((error) => {
            console.error(error.status, error.message);
            openErrorModal(error);
          })
          .finally(() => {
            loadingActive(false);
            onClose();
          });
      } else {
        requestBanMember({
          ticketId: ticketInfo.id,
          banReason: banReason,
          banTime: banTime,
        })
          .then((data) => {
            setTicketInformation({
              ticketData: data.ticket,
              userData: data.user,
            });
          })
          .catch((error) => {
            console.error(error.status, error.message);
            openErrorModal(error);
          })
          .finally(() => {
            loadingActive(false);
            onClose();
          });
      }
    } else {
      if (anonUser.type === "temp") {
        requestUnbanTempUser({ ticketId: ticketInfo.id })
          .then((data) => {
            setTicketInformation({
              ticketData: data.ticket,
              userData: data.user,
            });
          })
          .catch((error) => {
            console.error(error.status, error.message);
            openErrorModal(error);
          })
          .finally(() => {
            loadingActive(false);
            onClose();
          });
      } else {
        requestUnbanMember({ ticketId: ticketInfo.id })
          .then((data) => {
            setTicketInformation({
              ticketData: data.ticket,
              userData: data.user,
            });
          })
          .catch((error) => {
            console.error(error.status, error.message);
            openErrorModal(error);
          })
          .finally(() => {
            loadingActive(false);
            onClose();
          });
      }
    }
  };

  const onClose = () => {
    setBanReasion("");
    setBanTime(banTimes["times.7_days"]);
    document.getElementById("ticket-ban-modal").close();
  };

  const submitCondition =
    anonUser.banned || (banReason.trim() !== "" && banTime !== null);

  return (
    <>
      <dialog id="ticket-ban-modal" className="modal">
        <div className="modal-box flex flex-col gap-3 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300">
          <h3 className="font-bold text-lg w-full text-center">
            {anonUser.banned ? t("Unban user") : t("Ban user")}
          </h3>
          <span
            className={`${anonUser.banned ? "text-green-500" : "text-red-600"}`}
          >
            {anonUser.email}
          </span>
          <span>
            {anonUser.banned
              ? t("The user will be able to submit new tickets.")
              : t("This user will be banned from submitting new tickets.")}
          </span>

          {!anonUser.banned && (
            <div className="flex flex-col gap-1">
              <TextLargeInput
                labelName={t("Reason")}
                placeHolder={t("Type your reasoning here")}
                inputVariable={banReason}
                inputSetVariable={setBanReasion}
                limit={200}
                classNames={"p-0"}
              />
              <div className="flex md:flex-auto md:flex-grow-0 md:flex-shrink-0 md:w-1/2 w-full px-2">
                <label className="form-control w-full">
                  <div className="label">
                    <span className="label-text text-base font-semibold text-gray-800 dark:text-gray-300">
                      {t("Select ban time")}
                    </span>
                  </div>
                  <select
                    required
                    className="select select-bordered select-md w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                    value={banTime || ""}
                    onChange={(e) => {
                      setBanTime(e.target.value);
                    }}
                  >
                    {Object.keys(banTimes).map((option, index) => (
                      <option key={index} value={banTimes[option]}>
                        {t(option)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          )}

          <div className="flex justify-center gap-2 mt-2">
            <button
              className="btn btn-md min-w-24 bg-primary hover:bg-primary/70 text-white border-gray-300 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
              disabled={!submitCondition}
              onClick={handleConfirm}
            >
              {t("Confirm")}
            </button>
            <button
              className="btn btn-md min-w-24 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500 border border-gray-300 dark:border-gray-600"
              onClick={onClose}
            >
              {t("Close")}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={onClose}>close</button>
        </form>
      </dialog>
    </>
  );
};

export const BanHistory = ({ ticketInfo, ticketUser }) => {
  const { t } = useTranslation("edit");
  const { devs } = useContext(AccountContext);
  const { openErrorModal } = useContext(ContentContext);
  const [banHistory, setBanHistory] = useState([]);
  const [ticketsData, setTicketsData] = useState({});
  const [openHistory, setOpenHistory] = useState(false);

  useEffect(() => {
    if (ticketInfo?.ticket_type === "member" || ticketInfo?.type === "normal") {
      setOpenHistory(false);
      requestBanHistoryMember({
        memberId:
          ticketInfo?.ticket_type === "member"
            ? ticketInfo.user_id
            : ticketInfo.id,
      })
        .then((data) => {
          setBanHistory(data.ban_logs);
          setTicketsData(data.tickets_data);
        })
        .catch((error) => {
          console.error(error.status, error.message);
          openErrorModal(error);
        });
    } else if (ticketInfo.email) {
      setOpenHistory(false);
      requestBanHistoryTemp({ email: ticketInfo.email })
        .then((data) => {
          setBanHistory(data.ban_logs);
          setTicketsData(data.tickets_data);
        })
        .catch((error) => {
          console.error(error.status, error.message);
          openErrorModal(error);
        });
    }
  }, [ticketInfo?.email, ticketInfo?.banned, ticketUser?.banned]);

  const handleOpen = () => {
    setOpenHistory(!openHistory);
  };

  if (!ticketInfo.email || banHistory.length === 0) {
    return <></>;
  }

  return (
    <div className="flex flex-col gap-2 relative">
      <button
        className={`btn btn-sm max-h-10 max-w-[300px] ${darkAndLightThemeDefault}`}
        onClick={handleOpen}
      >
        {t("Ban History")}
      </button>

      {openHistory && (
        <div className="flex flex-col p-4 rounded-2xl absolute top-full mb-4 left-0 bg-white dark:bg-slate-800 w-max min-w-[320px] max-h-96 border border-slate-200 dark:border-slate-700 shadow-2xl overflow-y-auto z-[60]">
          {banHistory.map((banLog, index) => (
            <>
              <div
                key={banLog.id}
                className={`flex flex-col gap-1 ${banLog.active ? "text-red-600" : ""}`}
              >
                <div className="flex items-center flex-wrap">
                  <span className="min-w-36 max-w-36 text-wrap">
                    {t("Ticket")}
                  </span>
                  {/* <span>{banLog.ticket_id}</span> */}
                  <div className="relative text-gray-800 dark:text-gray-300 z-50">
                    <TicketButton ticketItems={ticketsData[banLog.ticket_id]} />
                  </div>
                </div>
                <div className="flex items-center flex-wrap">
                  <span className="min-w-36 max-w-36 text-wrap">
                    {t("Reason")}
                  </span>
                  <span>{banLog.reason}</span>
                </div>
                <div className="flex items-center flex-wrap">
                  <span className="min-w-36 max-w-36 text-wrap">
                    {t("Banned By")}
                  </span>
                  <span>
                    {devs[banLog.banner_id]?.name ?? banLog.banner_id}
                  </span>
                </div>
                <div className="flex items-center flex-wrap">
                  <span className="min-w-36 max-w-36 text-wrap">
                    {t("Banned for")}
                  </span>
                  <span>
                    {t(`times.${banLog.ban_time.replaceAll(" ", "_")}`)}
                  </span>
                </div>
                <div className="flex items-center flex-wrap">
                  <span className="min-w-36 max-w-36 text-wrap">
                    {t("Ban Date")}
                  </span>
                  <span>{formatTimestamp(banLog.timestamp * 1000)}</span>
                </div>
                {banLog.until !== -1 && (
                  <div className="flex items-center flex-wrap">
                    <span className="min-w-36 max-w-36 text-wrap">
                      {t("Ban Until")}
                    </span>
                    <span>{formatTimestamp(banLog.until * 1000)}</span>
                  </div>
                )}
                {banLog.ban_lifter_id !== "" && (
                  <>
                    <div className="flex items-center flex-wrap">
                      <span className="min-w-36 max-w-36 text-wrap">
                        {t("Unbanned By")}
                      </span>
                      <span>
                        {devs[banLog.ban_lifter_id]?.name ??
                          banLog.ban_lifter_id}
                      </span>
                    </div>
                    <div className="flex items-center flex-wrap">
                      <span className="min-w-36 max-w-36 text-wrap">
                        {t("Unban Date")}
                      </span>
                      <span>
                        {formatTimestamp(banLog.ban_lift_timestamp * 1000)}
                      </span>
                    </div>
                  </>
                )}
              </div>
              {banHistory.length - 1 !== index && (
                <div className="divider"></div>
              )}
            </>
          ))}
        </div>
      )}
    </div>
  );
};
